import React, { useCallback } from 'react';

import { eventPreventDefault } from '@lyearn/core/utils/events';
import { useTranslation } from '@lyearn/i18n';
import { Button, DeprecatedIconButton, Tooltip } from '@lyearn/ui';
import useBookmarkTooltip from '@/features/bookmark/hooks/useBookmarkTooltip';
import useContentBookmark, {
  UseContentBookmarkParams,
} from '@/features/bookmark/hooks/useContentBookmark';
import { useHeroContext } from '@/modules/corporate/components/DetailPageComponents/DetailHero/context';
import {
  ClassnameProps,
  HeroComposition,
} from '@/modules/corporate/components/DetailPageComponents/DetailHero/types';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';
import { usePreviewRoute } from '@/pages/corporate/routes/hooks/usePreviewRoutes';

import { BOOKMARKED_CONFIG, NOT_BOOKMARKED_CONFIG } from './utils';

const BookmarkAction: React.FC<ClassnameProps & UseContentBookmarkParams> = (props) => {
  const { contentId, contentType, bookmarkId, isDisabled, showLabel, ...rest } = props;
  const { fetching, onToggleBookmark, iconAnimationRef, updatedBookmarkId } = useContentBookmark({
    contentId,
    contentType,
    bookmarkId,
  });
  const { t } = useTranslation();
  const { open, onOpen, onClose, onDisable } = useBookmarkTooltip();
  const { Icon, tooltipKey } = updatedBookmarkId ? BOOKMARKED_CONFIG : NOT_BOOKMARKED_CONFIG;

  const onClick = useCallback(
    (event: any) => {
      eventPreventDefault(event);
      onDisable();
      onToggleBookmark();
    },
    [onDisable, onToggleBookmark],
  );

  if (!showLabel) {
    return (
      <Button
        className="!border-interactive-border hover:!border-border-dark"
        data-action={updatedBookmarkId ? 'Remove Bookmark' : 'Bookmark'}
        disabled={isDisabled || fetching}
        startIcon={<Icon className={iconAnimationRef.current} />}
        variant="outlined"
        onClick={onClick}
        {...rest}>
        {t(tooltipKey)}
      </Button>
    );
  }

  return (
    <Tooltip
      open={open}
      placement="right"
      title={`${t(tooltipKey)}`}
      onClose={onClose}
      onOpen={onOpen}>
      <DeprecatedIconButton
        appearance="secondary"
        data-action={updatedBookmarkId ? 'Remove Bookmark' : 'Bookmark'}
        disabled={isDisabled || fetching}
        variant="outlined"
        onClick={onClick}
        {...rest}>
        <Icon className={iconAnimationRef.current} />
      </DeprecatedIconButton>
    </Tooltip>
  );
};

const Bookmark: HeroComposition['Bookmark'] = (props) => {
  const { content } = useHeroContext();
  const isAdminRoute = useAdminRoute();
  const isPreviewRoute = usePreviewRoute();
  const { contentId, contentType, bookmark } = content;

  if ((isAdminRoute && !isPreviewRoute) || !contentId || !contentType || bookmark === undefined) {
    return null;
  }

  return (
    <BookmarkAction
      bookmarkId={bookmark?.id}
      contentId={contentId}
      contentType={contentType}
      isDisabled={isPreviewRoute}
      {...props}
    />
  );
};

export default Bookmark;
